import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, LocateFixed, BookmarkPlus, SearchX, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefData";
import { catLabel } from "@/components/CategoryIcon";
import { ListingCard, ListingSkeleton } from "@/components/ListingCard";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const CONDS = ["new", "like_new", "good", "fair", "for_parts"];

const Check = ({ label, checked, onChange, testid }) => (
  <label className="flex cursor-pointer items-center gap-2.5 py-1 text-sm">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="h-4 w-4 accent-[#3B1272]" />{label}
  </label>
);

const Filters = ({ params, set }) => {
  const { t, lang } = useI18n();
  const { categories, cities } = useRefData();
  const conds = (params.get("condition") || "").split(",").filter(Boolean);
  const [pmin, setPmin] = useState(params.get("price_min") || "");
  const [pmax, setPmax] = useState(params.get("price_max") || "");
  useEffect(() => {
    setPmin(params.get("price_min") || "");
    setPmax(params.get("price_max") || "");
  }, [params]);
  const toggleCond = (c, on) => set({ condition: (on ? [...conds, c] : conds.filter((x) => x !== c)).join(",") });
  const locate = () => {
    if (!navigator.geolocation) return toast.error(t("search.geoDenied"));
    navigator.geolocation.getCurrentPosition(
      (p) => set({ lat: p.coords.latitude.toFixed(4), lng: p.coords.longitude.toFixed(4), city: "", sort: "distance" }),
      () => toast.error(t("search.geoDenied")),
      { timeout: 8000 }
    );
  };
  const hasGeo = params.get("lat") || params.get("city");
  return (
    <div className="space-y-6" data-testid="search-filters">
      <div>
        <p className="label">{t("search.category")}</p>
        <select className="field" value={params.get("category") || ""} onChange={(e) => set({ category: e.target.value })} data-testid="filter-category">
          <option value="">{t("search.any")}</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{catLabel(c, lang)}</option>)}
        </select>
      </div>
      <div>
        <p className="label">{t("search.location")}</p>
        <select className="field" value={params.get("lat") ? "__me" : params.get("city") || ""} onChange={(e) => e.target.value !== "__me" && set({ city: e.target.value, lat: "", lng: "" })} data-testid="filter-city">
          <option value="">{t("hero.cityPh")}</option>
          {params.get("lat") && <option value="__me">{t("search.myLocation")}</option>}
          {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <button onClick={locate} className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-[#3B1272]" data-testid="filter-use-location"><LocateFixed className="h-4 w-4" />{t("search.useMyLocation")}</button>
        {hasGeo && (
          <select className="field mt-2" value={params.get("radius_km") || ""} onChange={(e) => set({ radius_km: e.target.value })} data-testid="filter-radius">
            <option value="">{t("search.radius")}: {t("search.noLimit")}</option>
            {[5, 10, 25, 50, 100, 250].map((r) => <option key={r} value={r}>{r} km</option>)}
          </select>
        )}
      </div>
      <div>
        <p className="label">{t("search.price")}</p>
        <div className="flex gap-2">
          <input className="field" type="number" min="0" placeholder={t("search.min")} value={pmin} onChange={(e) => setPmin(e.target.value)} onBlur={() => set({ price_min: pmin })} data-testid="filter-price-min" />
          <input className="field" type="number" min="0" placeholder={t("search.max")} value={pmax} onChange={(e) => setPmax(e.target.value)} onBlur={() => set({ price_max: pmax })} data-testid="filter-price-max" />
        </div>
      </div>
      <div>
        <p className="label">{t("search.condition")}</p>
        {CONDS.map((c) => <Check key={c} label={t(`cond.${c}`)} checked={conds.includes(c)} onChange={(on) => toggleCond(c, on)} testid={`filter-cond-${c}`} />)}
      </div>
      <div>
        <p className="label">{t("search.sellerType")}</p>
        <select className="field" value={params.get("seller_type") || ""} onChange={(e) => set({ seller_type: e.target.value })} data-testid="filter-seller-type">
          <option value="">{t("search.any")}</option>
          {["juppo", "private", "professional"].map((s) => <option key={s} value={s}>{t(`seller.${s}`)}</option>)}
        </select>
      </div>
      <div>
        <p className="label">{t("search.delivery")}</p>
        <select className="field" value={params.get("delivery") || ""} onChange={(e) => set({ delivery: e.target.value })} data-testid="filter-delivery">
          <option value="">{t("search.any")}</option>
          {["shipping", "pickup"].map((s) => <option key={s} value={s}>{t(`delivery.${s}`)}</option>)}
        </select>
      </div>
      <div>
        <p className="label">{t("search.special")}</p>
        <Check label={t("search.outlet")} checked={params.get("area") === "outlet"} onChange={(on) => set({ area: on ? "outlet" : "" })} testid="filter-outlet" />
        <Check label={t("search.deals")} checked={params.get("deals") === "true"} onChange={(on) => set({ deals: on ? "true" : "" })} testid="filter-deals" />
        <Check label={t("search.coin")} checked={params.get("coin") === "true"} onChange={(on) => set({ coin: on ? "true" : "" })} testid="filter-coin" />
        <Check label={t("search.exchange")} checked={params.get("exchange") === "true"} onChange={(on) => set({ exchange: on ? "true" : "" })} testid="filter-exchange" />
      </div>
    </div>
  );
};

export default function SearchPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const qs = params.toString();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["search", qs], queryFn: async () => (await api.get(`/listings?${qs}`)).data, keepPreviousData: true });
  const cat = params.get("category");
  const { data: showcase } = useQuery({ queryKey: ["showcase", cat], enabled: !!cat, queryFn: async () => (await api.get(`/listings?promo=showcase&category=${cat}&page_size=4`)).data });

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v === "" || v == null ? next.delete(k) : next.set(k, v)));
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };
  const saveSearch = async () => {
    try {
      await api.post("/saved-searches", { name: params.get("q") || t("search.title"), params: Object.fromEntries(params.entries()) });
      toast.success(t("search.saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sorts = ["relevance", "newest", "price_asc", "price_desc", "distance"];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="search-page">
      <div className="flex flex-wrap items-center gap-3">
        <form className="flex flex-1 items-center gap-2" onSubmit={(e) => { e.preventDefault(); set({ q: new FormData(e.target).get("q") }); }}>
          <input name="q" key={params.get("q")} defaultValue={params.get("q") || ""} placeholder={t("hero.searchPh")} className="field !py-3" data-testid="search-query-input" />
          <button className="btn-violet !py-3" data-testid="search-submit-btn">{t("hero.search")}</button>
        </form>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#64748B]" data-testid="search-total"><span className="font-display text-lg font-bold text-[#1E1B2E]">{data?.total ?? "…"}</span> {t("search.results")}</p>
        <div className="flex items-center gap-2">
          {user && !["admin", "moderator", "operator"].includes(user.role) && (
            <button onClick={saveSearch} className="flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-semibold" data-testid="save-search-btn"><BookmarkPlus className="h-4 w-4" />{t("search.save")}</button>
          )}
          <select className="field !w-auto !rounded-full" value={params.get("sort") || "relevance"} onChange={(e) => set({ sort: e.target.value })} data-testid="search-sort-select">
            {sorts.map((s) => <option key={s} value={s} disabled={s === "distance" && !params.get("lat") && !params.get("city")}>{t(`search.sorts.${s}`)}</option>)}
          </select>
          <Sheet>
            <SheetTrigger className="flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-semibold lg:hidden" data-testid="mobile-filters-btn"><SlidersHorizontal className="h-4 w-4" />{t("search.filters")}</SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto bg-white"><div className="mt-6"><Filters params={params} set={set} /></div></SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="mt-6 grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl bg-white p-5 shadow-[0_4px_20px_-4px_rgba(59,18,114,0.08)]">
            <div className="mb-4 flex items-center justify-between"><p className="font-display font-bold">{t("search.filters")}</p>
              <button onClick={() => setParams(new URLSearchParams())} className="text-xs font-semibold text-[#FF7A6B]" data-testid="filters-reset-btn">{t("search.reset")}</button></div>
            <Filters params={params} set={set} />
          </div>
        </aside>
        <div>
          {cat && showcase?.items?.length > 0 && (
            <div className="mb-6 rounded-3xl bg-[#FFF7E6] p-4" data-testid="category-showcase">
              <p className="mb-3 font-display font-bold text-[#1E1B2E]">{t("p4.home.showcase")}</p>
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">{showcase.items.map((l) => <ListingCard key={l.listing_id} l={l} />)}</div>
            </div>
          )}
          {isError ? (
            <div className="py-20 text-center"><p>{t("common.error")}</p><button onClick={() => refetch()} className="btn-violet mt-4">{t("common.retry")}</button></div>
          ) : isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-5 xl:grid-cols-3">{[...Array(6)].map((_, i) => <ListingSkeleton key={i} />)}</div>
          ) : data.items.length === 0 ? (
            <div className="flex flex-col items-center rounded-3xl bg-white py-20 text-center" data-testid="search-empty">
              <SearchX className="h-12 w-12 text-[#C4B5FD]" /><p className="mt-4 font-display text-lg font-bold">{t("search.empty")}</p><p className="mt-1 text-sm text-[#64748B]">{t("search.emptyHint")}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-5 xl:grid-cols-3" data-testid="search-results">{data.items.map((l) => <ListingCard key={l.listing_id} l={l} />)}</div>
              {data.pages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-3" data-testid="search-pagination">
                  <button disabled={page <= 1} onClick={() => set({ page: page - 1 })} className="btn-violet !px-3" data-testid="page-prev-btn"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm font-semibold">{t("search.page")} {page} / {data.pages}</span>
                  <button disabled={page >= data.pages} onClick={() => set({ page: page + 1 })} className="btn-violet !px-3" data-testid="page-next-btn"><ChevronRight className="h-4 w-4" /></button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
